import { useState } from "react";
import {
  Dialog,
  DialogPanel,
  DialogBackdrop,
  TransitionChild,
} from "@headlessui/react";
import logo from "./assets/logo_transparent.png";
import Introduction from "./sections/Introduction";
import GettingStarted from "./sections/GettingStarted";
import APIReference from "./sections/APIReference";
import Resources from "./sections/Resources";

import {
  Bars3Icon,
  CalendarIcon,
  ChartPieIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const sections = [
  {
    name: "Introduction",
    component: Introduction,
  },
  { name: "Getting Started", component: GettingStarted },
  { name: "API Reference", component: APIReference },
  { name: "Resources", component: Resources },
];

export function DocumentationSidebar({
  selectedSection,
  setSelectedSection,
  setSidebarOpen,
}) {
  return (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-50 border-r px-6 pb-2">
      <div className="flex px-2 mt-4 h-16 shrink-0 items-center">
        <a href="/">
          <img className="h-12 w-auto" src={logo} alt="" />
        </a>
      </div>
      <nav className="flex flex-1 flex-col">
        <ul
          role="list"
          className="flex flex-1 flex-col gap-y-2 text-lg text-gray-600"
        >
          {sections.map((section) => (
            <li
              key={section.name}
              className={`cursor-pointer px-2 py-1 rounded ${
                selectedSection === section.name
                  ? "font-semibold text-blue-600 bg-blue-50"
                  : "hover:bg-gray-200"
              }`}
              onClick={() => {
                setSelectedSection(section.name);
                if (setSidebarOpen) setSidebarOpen(false);
              }}
            >
              {section.name}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

export function Documentation() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState("Introduction");

  const currentSection =
    sections.find((s) => s.name === selectedSection)?.component || null;

  return (
    <>
      <div>
        <Dialog
          open={sidebarOpen}
          onClose={setSidebarOpen}
          className="relative z-50 lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="-m-2.5 p-2.5"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      aria-hidden="true"
                      className="size-6 text-white"
                    />
                  </button>
                </div>
              </TransitionChild>
              {/* Sidebar component, swap this element with another sidebar if you like */}
              <DocumentationSidebar
                setSidebarOpen={setSidebarOpen}
                setSelectedSection={setSelectedSection}
              />
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <DocumentationSidebar setSelectedSection={setSelectedSection} />
        </div>

        <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-gray-50 text-gray-700 border-b px-4 py-4 shadow-xs sm:px-6 lg:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="-m-2.5 p-2.5 lg:hidden"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
          <div className="flex-1 text-md font-semibold">
            Whenfin Documentation
          </div>
        </div>

        <main className="py-10 lg:pl-72">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="mx-auto max-w-3xl">
                {currentSection(setSelectedSection)}
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
